/**
 * MacOSIncrementNamer -- macOS-style "(N)" auto-increment naming.
 *
 * Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
 * Pebble: Phase 4.4 bulk action bar + duplicate modal (P4-Q12).
 *
 * Mirrors the Python helpers in
 *   - api/src/services/artwork_cascade_service.py::next_available_name
 *     (P3-Q5 fork naming)
 *   - api/src/services/decoration_set_service.py::next_available_set_name
 *     (P4-Q1 decoration-set duplicate)
 *
 * Same algorithm, expressed in JS so the frontend can:
 *   - Show a name preview in the Bulk Duplicate modal before submit.
 *   - Detect collisions client-side for fast feedback when the caller
 *     already has the list of existing names in hand (e.g. the
 *     products-list page already loaded all CampaignProducts).
 *
 * The backend is the authority -- the server runs the equivalent
 * Python collision check inside the transaction. This helper is a
 * preview / UX affordance, NOT a security boundary.
 */

/**
 * Strip a trailing " (N)" from ``name`` so multi-duplicate chains
 * converge on a consistent base.
 *
 * Examples
 * --------
 *   "Product A"        -> "Product A"
 *   "Product A (1)"    -> "Product A"
 *   "Product A (12)"   -> "Product A"
 *   "Logo (a)"         -> "Logo (a)"      (non-digit in parens)
 *   "Logo (1).png"     -> "Logo (1).png"  (mid-name parens; not trailing)
 *   "Product(1)"       -> "Product(1)"    (no space before paren)
 *
 * @param {string|null|undefined} name
 * @returns {string}
 */
export function stripTrailingIncrement(name) {
    if (name === null || name === undefined) return ''
    if (typeof name !== 'string') return ''
    // Don't trim -- a leading/trailing space is part of the name; we
    // only care about a trailing " (N)" suffix attached to the rstrip
    // tail. Mirror the Python: rstrip() once on the comparison name
    // but keep the original for return when no strip happens.
    const trimmed = name.replace(/\s+$/, '')
    if (!trimmed.endsWith(')')) return name
    // Walk backwards over a digit run inside parens.
    let i = trimmed.length - 2  // position just before closing ')'
    if (i < 0 || !/[0-9]/.test(trimmed[i])) return name
    while (i >= 0 && /[0-9]/.test(trimmed[i])) {
        i -= 1
    }
    // After the digit run we expect "( " (open-paren preceded by a space)
    // so the suffix is " (N)" not just "(N)".
    if (i < 1 || trimmed[i] !== '(' || trimmed[i - 1] !== ' ') return name
    return trimmed.slice(0, i - 1)
}

/**
 * Return the next non-colliding "{base} (N)" for the supplied
 * ``baseName``.
 *
 * The base is stripped of a trailing " (M)" first so a chain of
 * duplicates does not produce "Logo (1) (1)" -- it converges on
 * "Logo (2)".
 *
 * @param {string|null|undefined} baseName
 * @param {Iterable<string>|Set<string>|string[]} existingNames
 * @returns {string}  The first free "{base} (N)" candidate.
 */
export function nextAvailableName(baseName, existingNames) {
    const cleanedBase = stripTrailingIncrement(baseName)
    const base = cleanedBase && cleanedBase.length > 0 ? cleanedBase : 'Untitled'

    // Coerce existingNames to a Set for O(1) lookup. Accept Array, Set,
    // or any iterable.
    const taken = existingNames instanceof Set
        ? existingNames
        : new Set(existingNames || [])

    let n = 1
    // Bounded probe -- matches the Python ceiling. 100000 collisions
    // is implausible and infinite-loop guard.
    while (n <= 100000) {
        const candidate = `${base} (${n})`
        if (!taken.has(candidate)) {
            return candidate
        }
        n += 1
    }
    throw new Error(
        `Could not find a free name for '${baseName}' after 100000 candidates.`,
    )
}
